import { PullEditorState } from '@v2/components/flowsteps/editors/common/states/pull-editor-state';
import { Flowstep, Operation, ProviderType, PullMode } from 'shared/types/flowstep';
import { capitalizeString } from 'shared/utils/utils';
import { reaction, toJS } from 'mobx';
import { makeAutoObservableExt } from '@v2/utils/mobx/make-auto-observable-ext';
import { DataObject } from 'shared/providers/types';
import { AsyncLoadable } from '@v2/utils/mobx/async-loadable';
export class PullSalesforceReportState extends PullEditorState {
  reportSearchString = '';
  reportSearchResult = new AsyncLoadable<DataObject[]>([]);
  savedReportName = '';
  savedExcludedFieldNames: string[] | null = null;

  constructor(providerType: ProviderType = ProviderType.SalesforceReport) {
    super({ operation: Operation.PULL, provider: providerType });
    makeAutoObservableExt(this, PullSalesforceReportState, { validate: false });

    reaction(
      () => [this.pullSettings.objectLists.status, this.savedReportName],
      () => {
        const objectList = this.pullSettings.objectLists;
        if (objectList.isReady && this.savedReportName) {
          const isReportInList = objectList.data.some((report) => report.id === this.pullSettings._selectedObjectId);

          if (!isReportInList) {
            const savedReport = {
              id: this.pullSettings._selectedObjectId,
              name: this.savedReportName,
              label: this.savedReportName,
            } as DataObject;
            objectList.data = [...objectList.data, savedReport];
            this.pullSettings.selectedObject = savedReport;
          }
        }
      },
    );

    reaction(
      () => [this.pullSettings.objectFields.data, this.savedExcludedFieldNames] as const,
      ([objectFields, savedExcludedFieldNames]) => {
        if (!objectFields?.length) return;
        if (savedExcludedFieldNames !== null) {
          this.pullSettings.selectedFieldNames = objectFields
            .filter((field) => !savedExcludedFieldNames.includes(field.name))
            .map((field) => field.name);
          this.savedExcludedFieldNames = null;
        } else if (!this.pullSettings.selectedFieldNames.length) {
          this.pullSettings.selectedFieldNames = objectFields.map((field) => field.name);
        }
      },
    );

    // Before XLC-1062, condition.fieldName was saved from ReportField.name
    // ("account name"); after the migration it's ReportField.key ("ACCOUNT.NAME"),
    // which is required for excludedProps to match the backend's column-key lookup.
    // Rewrite legacy fieldNames so that ConditionEditor can resolve and highlight
    // the selected field on flowsteps saved with the old format.
    reaction(
      () => this.pullSettings.objectFields.data,
      (objectFields) => {
        if (!objectFields?.length) return;
        for (const condition of this.pullSettings.conditions) {
          const legacy = objectFields.find((f) => f.originalField?.name === condition.fieldName);
          if (legacy) {
            condition.fieldName = legacy.name;
          }
        }
      },
    );
  }

  isSimilarFlowstep(flowstep: Flowstep): boolean {
    return flowstep.operation === Operation.PULL && flowstep.provider === ProviderType.SalesforceReport;
  }

  validate() {
    this.pullSettings.validate(this.validationErrors);
  }

  get isFlowstepButtonsDisabled() {
    return (
      !this.pullSettings.selectedObject ||
      this.validationErrors.size > 0 ||
      (this.pullSettings.selectedFieldNames.length === 0 && this.pullSettings.objectFields.data.length > 0)
    );
  }

  get generatedFlowstepName() {
    return `Pull from ${capitalizeString(this.pullSettings.selectedObject?.name)}`;
  }

  get excludedFieldNames() {
    return this.pullSettings.objectFields.data
      .filter((objectField) => !this.pullSettings.selectedFieldNames.includes(objectField.name))
      .map((objectField) => objectField.name);
  }

  get reports(): DataObject[] {
    const searchResult = this.reportSearchResult.data;
    const objectList = this.pullSettings.objectLists.data;
    return this.reportSearchString ? searchResult : objectList;
  }

  get isLoadingReports() {
    return this.reportSearchString ? this.reportSearchResult.isLoading : this.pullSettings.objectLists.isLoading;
  }

  setReportSearchString(searchString: string) {
    this.reportSearchString = searchString;
    this.reportSearchResult.load(async () => this.pullSettings.provider.loadObjectTypes({ query: searchString }));
  }

  setSelectedReport(report?: DataObject) {
    if (report) {
      const isInList = this.pullSettings.objectLists.data.some((item) => item.id === report.id);
      !isInList && this.pullSettings.objectLists.data.push(report);
    }
    if (this.pullSettings.selectedObject?.id !== report?.id) {
      this.pullSettings.selectedFieldNames = [];
    }
    this.pullSettings.selectedObject = report;
    if (this.reportSearchString) {
      this.reportSearchString = '';
    }
  }

  public buildFlowstep(): Partial<Flowstep['options']> {
    const flowstep = super.buildFlowstep();

    return {
      id: this.flowstep?.id,
      flowId: this.flowstep?.flowId,
      name: this.name || this.generatedFlowstepName,
      errorReaction: this.errorReaction,
      operation: Operation.PULL,
      provider: this.flowstepMeta.provider,
      version: '4',
      options: {
        ...flowstep.options,
        target: this.pullOptions.startCell,
        worksheetId: this.worksheetId,
        pullMode: this.pullSettings.pullMode,
        withDateTimeColumn: this.pullSettings.withDateTimeColumn,
        createDataTable: this.pullOptions.createExcelTable,
        useDropdownsForPicklists: this.pullOptions.createDropDowns,
      },
      providerOptions: {
        objectType: this.pullSettings.selectedObjectId || '',
        objectName: this.pullSettings.selectedObject?.name || this.savedReportName || '',
        conditions: toJS(this.pullSettings.conditions),
        excludedProps: toJS(this.excludedFieldNames),
      },
    };
  }

  public onFlowstepLoaded(flowstep: Flowstep, meta: { isExact?: boolean; flowstepTarget?: any }): void {
    super.onFlowstepLoaded(flowstep, meta);

    const { options = {}, providerOptions = {} } = flowstep;
    const { objectType = '', objectName = '', excludedProps, conditions = [] } = providerOptions;
    this.savedExcludedFieldNames = excludedProps ?? null;

    this.savedReportName = objectName;

    this.pullSettings.createNewSheet = false;
    this.pullSettings.selectedObjectId = objectType;
    this.pullSettings.conditions = conditions;
    this.worksheetId = options.worksheetId;

    this.pullSettings.pullMode = options.pullMode ?? PullMode.OVERWRITE;
    this.pullSettings.withDateTimeColumn = options.withDateTimeColumn ?? true;
    this.pullOptions.startCell = options.target ?? 'A1';
    this.pullOptions.createExcelTable = options.createDataTable ?? true;
    this.pullOptions.createDropDowns = options.useDropdownsForPicklists ?? true;
  }
}
